/**
 * Comments Sidebar Component
 *
 * Right sidebar for viewing, creating, and managing PDF document comments.
 * All comment creation/editing happens here (not in popups on the PDF).
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import styles from './CommentsSidebar.module.scss';
import { ReactComponent as CloseIcon } from '../../shared/icons/close-icon.svg';
import { ReactComponent as ReplyIcon } from '../../shared/icons/icon-arrow-circle.svg';

export interface Comment {
  id: string;
  // TWO-PHASE PAGE TRACKING
  pageId: string;    // Stable page UUID
  pageNum: number;   // Derived from pageId
  x: number;
  y: number;
  text: string;
  author: string;
  authorId: string;
  createdAt: string;
  modifiedAt?: string;
  mentions?: string[]; // User IDs mentioned in comment
  parentId?: string; // For threaded replies
  replies?: Comment[]; // Child comments (populated by parent)
}

export interface CommentsSidebarProps {
  visible: boolean;
  onClose: () => void;
  comments: Comment[];
  currentUserId: string;
  currentUserName: string;
  onAddComment: (comment: Omit<Comment, 'id' | 'createdAt' | 'pageId'>) => void; // Handler adds id, createdAt, and pageId
  onEditComment: (commentId: string, newText: string) => void;
  onDeleteComment: (commentId: string) => void;
  onCommentClick: (comment: Comment) => void; // Scroll to page + highlight marker
  totalPages: number;
  selectedCommentId?: string | null; // For auto-opening edit mode on new comments
}

export const CommentsSidebar: React.FC<CommentsSidebarProps> = ({
  visible,
  onClose,
  comments,
  currentUserId,
  currentUserName,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onCommentClick,
  totalPages,
  selectedCommentId,
}) => {
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const commentRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Auto-edit new comments (those with empty text)
  React.useEffect(() => {
    if (selectedCommentId && visible) {
      const comment = comments.find(c => c.id === selectedCommentId);
      if (comment && comment.text === '') {
        // Auto-start editing for new comments
        setEditingCommentId(comment.id);
        setEditText('');
      }
    }
  }, [selectedCommentId, comments, visible]);

  // Scroll to selected comment when clicked from document
  React.useEffect(() => {
    if (selectedCommentId && visible && commentRefs.current[selectedCommentId]) {
      const commentElement = commentRefs.current[selectedCommentId];
      if (commentElement) {
        commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Add temporary highlight
        commentElement.style.backgroundColor = 'rgba(0, 108, 144, 0.1)';
        setTimeout(() => {
          commentElement.style.backgroundColor = '';
        }, 2000);
      }
    }
  }, [selectedCommentId, visible]);

  // Close menu when clicking outside
  React.useEffect(() => {
    if (openMenuId) {
      const handleClickOutside = () => setOpenMenuId(null);
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  // Build comment threads (parent-child relationships)
  const commentThreads = useMemo(() => {
    // Separate top-level comments from replies
    const topLevel = comments.filter(c => !c.parentId);
    const repliesMap = new Map<string, Comment[]>();

    // Group replies by parent ID
    comments.forEach(comment => {
      if (comment.parentId) {
        if (!repliesMap.has(comment.parentId)) {
          repliesMap.set(comment.parentId, []);
        }
        repliesMap.get(comment.parentId)!.push(comment);
      }
    });

    // Sort replies by creation time
    repliesMap.forEach(replies => {
      replies.sort((a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    });

    // Attach replies to parent comments
    return topLevel.map(comment => ({
      ...comment,
      replies: repliesMap.get(comment.id) || []
    }));
  }, [comments]);

  // Group threaded comments by page
  const commentsByPage = useMemo(() => {
    const grouped: Record<number, Comment[]> = {};
    commentThreads.forEach(comment => {
      if (!grouped[comment.pageNum]) {
        grouped[comment.pageNum] = [];
      }
      grouped[comment.pageNum].push(comment);
    });
    // Sort comments within each page by creation time
    Object.keys(grouped).forEach(pageNum => {
      grouped[Number(pageNum)].sort((a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    });
    return grouped;
  }, [commentThreads]);


  const handleStartEdit = useCallback((comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditText(comment.text);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingCommentId && editText.trim()) {
      onEditComment(editingCommentId, editText.trim());
      setEditingCommentId(null);
      setEditText('');
    }
  }, [editingCommentId, editText, onEditComment]);

  const handleCancelEdit = useCallback(() => {
    // If cancelling a new comment (empty text), delete it
    if (editingCommentId) {
      const comment = comments.find(c => c.id === editingCommentId);
      if (comment && comment.text === '') {
        onDeleteComment(editingCommentId);
      }
    }
    setEditingCommentId(null);
    setEditText('');
  }, [editingCommentId, comments, onDeleteComment]);

  const handleDeleteClick = useCallback((commentId: string) => {
    setDeleteConfirmId(commentId);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirmId) {
      onDeleteComment(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  }, [deleteConfirmId, onDeleteComment]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmId(null);
  }, []);

  const handleStartReply = useCallback((parentComment: Comment) => {
    setReplyingToId(parentComment.id);
    setReplyText('');
  }, []);

  const handleSaveReply = useCallback(() => {
    if (replyingToId && replyText.trim()) {
      const parentComment = comments.find(c => c.id === replyingToId);
      if (parentComment) {
        // Create reply comment with same position as parent
        onAddComment({
          pageNum: parentComment.pageNum,
          x: parentComment.x,
          y: parentComment.y,
          text: replyText.trim(),
          author: currentUserName,
          authorId: currentUserId,
          parentId: replyingToId,
        });
      }
      setReplyingToId(null);
      setReplyText('');
    }
  }, [replyingToId, replyText, comments, onAddComment, currentUserName, currentUserId]);

  const handleCancelReply = useCallback(() => {
    setReplyingToId(null);
    setReplyText('');
  }, []);

  const handleToggleMenu = useCallback((e: React.MouseEvent, commentId: string) => {
    e.stopPropagation();
    setOpenMenuId(prev => prev === commentId ? null : commentId);
  }, []);

  const handleMenuAction = useCallback((action: 'edit' | 'delete', comment: Comment) => {
    setOpenMenuId(null);
    if (action === 'edit') {
      handleStartEdit(comment);
    } else {
      handleDeleteClick(comment.id);
    }
  }, [handleStartEdit, handleDeleteClick]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (!visible) return null;

  return (
    <div className={styles.sidebar}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h3>Comments ({comments.length})</h3>
        </div>
        <button
          className={styles.closeButton}
          onClick={onClose}
          title="Close comments"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Comments List */}
      <div className={styles.commentsList}>
        {comments.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No comments yet</p>
          </div>
        ) : (
          <>
            {Object.keys(commentsByPage)
              .map(Number)
              .sort((a, b) => a - b)
              .map(pageNum => (
                <div key={pageNum} className={styles.pageGroup}>
                  <div className={styles.pageGroupHeader}>Page {pageNum}</div>
                  {commentsByPage[pageNum]
                    .filter(comment => comment.text !== '' || editingCommentId === comment.id)
                    .map(comment => (
                    <div
                      key={comment.id}
                      className={styles.commentItem}
                      ref={el => commentRefs.current[comment.id] = el}
                    >
                      <div className={styles.commentHeader}>
                        <div className={styles.commentAuthor}>
                          <div className={styles.authorAvatar}>
                            {comment.author.charAt(0).toUpperCase()}
                          </div>
                          <div className={styles.authorInfo}>
                            <div className={styles.authorName}>{comment.author}</div>
                            <div className={styles.commentDate}>
                              {formatDate(comment.modifiedAt || comment.createdAt)}
                            </div>
                          </div>
                        </div>
                        <div className={styles.headerActions}>
                          {/* Reply button */}
                          {comment.text !== '' && editingCommentId !== comment.id && (
                            <button
                              className={styles.replyButton}
                              onClick={() => handleStartReply(comment)}
                              title="Reply to comment"
                            >
                              <ReplyIcon className={styles.replyIcon} />
                              <span>Reply</span>
                            </button>
                          )}
                          {/* Three-dot menu for Edit/Delete */}
                          {editingCommentId !== comment.id && comment.authorId === currentUserId && comment.text !== '' && (
                            <div className={styles.menuContainer}>
                              <button
                                className={styles.menuButton}
                                onClick={(e) => handleToggleMenu(e, comment.id)}
                                title="More options"
                              >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                  <circle cx="8" cy="3" r="1.5"/>
                                  <circle cx="8" cy="8" r="1.5"/>
                                  <circle cx="8" cy="13" r="1.5"/>
                                </svg>
                              </button>
                              {openMenuId === comment.id && (
                                <div className={styles.dropdownMenu}>
                                  <button
                                    className={styles.menuItem}
                                    onClick={() => handleMenuAction('edit', comment)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className={styles.menuItem}
                                    onClick={() => handleMenuAction('delete', comment)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {editingCommentId === comment.id ? (
                        <div className={styles.editSection}>
                          <textarea
                            className={styles.editInput}
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            placeholder="Enter your comment..."
                            rows={3}
                            autoFocus
                          />
                          <div className={styles.editActions}>
                            <button
                              className={styles.saveButton}
                              onClick={handleSaveEdit}
                              disabled={!editText.trim()}
                            >
                              Save
                            </button>
                            <button
                              className={styles.cancelButton}
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div
                            className={styles.commentText}
                            onClick={() => onCommentClick(comment)}
                          >
                            {comment.text}
                          </div>

                          {/* Nested replies */}
                          {comment.replies && comment.replies.length > 0 && (
                            <div className={styles.repliesContainer}>
                              {comment.replies.map(reply => (
                                <div key={reply.id} className={styles.replyItem}>
                                  <div className={styles.commentHeader}>
                                    <div className={styles.commentAuthor}>
                                      <div className={styles.authorAvatar}>
                                        {reply.author.charAt(0).toUpperCase()}
                                      </div>
                                      <div className={styles.authorInfo}>
                                        <div className={styles.authorName}>{reply.author}</div>
                                        <div className={styles.commentDate}>
                                          {formatDate(reply.modifiedAt || reply.createdAt)}
                                        </div>
                                      </div>
                                    </div>
                                    {reply.authorId === currentUserId && (
                                      <div className={styles.menuContainer}>
                                        <button
                                          className={styles.menuButton}
                                          onClick={(e) => handleToggleMenu(e, reply.id)}
                                          title="More options"
                                        >
                                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                            <circle cx="8" cy="3" r="1.5"/>
                                            <circle cx="8" cy="8" r="1.5"/>
                                            <circle cx="8" cy="13" r="1.5"/>
                                          </svg>
                                        </button>
                                        {openMenuId === reply.id && (
                                          <div className={styles.dropdownMenu}>
                                            <button
                                              className={styles.menuItem}
                                              onClick={() => handleMenuAction('edit', reply)}
                                            >
                                              Edit
                                            </button>
                                            <button
                                              className={styles.menuItem}
                                              onClick={() => handleMenuAction('delete', reply)}
                                            >
                                              Delete
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {editingCommentId === reply.id ? (
                                    <div className={styles.editSection}>
                                      <textarea
                                        className={styles.editInput}
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                        placeholder="Enter your reply..."
                                        rows={2}
                                        autoFocus
                                      />
                                      <div className={styles.editActions}>
                                        <button
                                          className={styles.saveButton}
                                          onClick={handleSaveEdit}
                                          disabled={!editText.trim()}
                                        >
                                          Save
                                        </button>
                                        <button
                                          className={styles.cancelButton}
                                          onClick={handleCancelEdit}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className={styles.commentText}>
                                      {reply.text}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Reply input form */}
                          {replyingToId === comment.id && (
                            <div className={styles.replyForm}>
                              <textarea
                                className={styles.editInput}
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Write a reply..."
                                rows={2}
                                autoFocus
                              />
                              <div className={styles.editActions}>
                                <button
                                  className={styles.saveButton}
                                  onClick={handleSaveReply}
                                  disabled={!replyText.trim()}
                                >
                                  Reply
                                </button>
                                <button
                                  className={styles.cancelButton}
                                  onClick={handleCancelReply}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ))}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className={styles.modalOverlay} onClick={handleCancelDelete}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h4>Delete Comment</h4>
            </div>
            <div className={styles.modalBody}>
              <p>Are you sure you want to delete this comment? This action cannot be undone.</p>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.modalCancelButton}
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
              <button
                className={styles.modalDeleteButton}
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
